import { FC, useState } from "react";
import { Box, Typography, IconButton, Tooltip, Stack } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useQueryClient } from "@tanstack/react-query";
import { AttachmentMetaDto } from "../../../api/Api";
import { useRoles } from "../../../auth/useRoles";
import { canManageEntity } from "../../../auth/permissions";
import { useIsEntityMerger } from "../../../auth/entityMergerContext";
import { DeleteConfirmDialog } from "./deleteConfirmDialog";
import { LifecycleAttachmentApi } from "../types";

type Props = {
  entityId: string;
  lifecycleName: string;
  attachments: AttachmentMetaDto[];
  attachmentApi: LifecycleAttachmentApi;
};

const EXT_COLORS: Record<string, { bg: string; color: string; label: string }> =
  {
    pdf: { bg: "#fdecea", color: "#c62828", label: "PDF" },
    xlsx: { bg: "#e8f5e9", color: "#2e7d32", label: "XLSX" },
    xls: { bg: "#e8f5e9", color: "#2e7d32", label: "XLS" },
    docx: { bg: "#e3f2fd", color: "#1565c0", label: "DOC" },
    doc: { bg: "#e3f2fd", color: "#1565c0", label: "DOC" },
    png: { bg: "#f3e5f5", color: "#6a1b9a", label: "PNG" },
    jpg: { bg: "#f3e5f5", color: "#6a1b9a", label: "JPG" },
    jpeg: { bg: "#f3e5f5", color: "#6a1b9a", label: "JPG" }
  };

function getExt(fileName: string) {
  return fileName.split(".").pop()?.toLowerCase() ?? "";
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export const AttachmentList: FC<Props> = ({
  entityId,
  lifecycleName,
  attachments,
  attachmentApi
}) => {
  const download = attachmentApi.useDownload();
  const roles = useRoles();
  const canDelete = canManageEntity(roles, useIsEntityMerger());
  const queryClient = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const remove = attachmentApi.useDelete({
    onSuccess: () => {
      setPendingDelete(null);
      queryClient.invalidateQueries({
        queryKey: attachmentApi.listKey(entityId, lifecycleName)
      });
    }
  });

  if (attachments.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4, color: "text.disabled" }}>
        <Typography variant="body2">No files uploaded yet</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            color: "text.disabled",
            textTransform: "uppercase",
            mb: 1.5,
            display: "block"
          }}
        >
          Uploaded files
        </Typography>

        <Stack spacing={1}>
          {attachments.map((file) => {
            const ext = getExt(file.fileName || "");
            const style = EXT_COLORS[ext] ?? {
              bg: "#eceff1",
              color: "#546e7a",
              label: ext.toUpperCase() || "FILE"
            };

            return (
              <Box
                key={file.fileName}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" }
                }}
              >
                {/* Type badge */}
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: style.bg,
                    color: style.color,
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: 0.3
                  }}
                >
                  {style.label}
                </Box>

                {/* Name + meta */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    noWrap
                    title={file.fileName}
                    sx={{ color: "text.primary" }}
                  >
                    {file.fileName}
                  </Typography>
                  <Box>
                    <Typography variant="caption" color="text.disabled">
                      {file.uploader} &nbsp;·&nbsp;{" "}
                      {formatDate(file.lastModified || "")} &nbsp;·&nbsp;{" "}
                      {formatBytes(file.sizeBytes || 0)}
                    </Typography>
                  </Box>
                </Box>

                {/* Actions */}
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    disabled={download.isPending}
                    onClick={() =>
                      download.mutate({
                        entityId,
                        lifecycleName,
                        fileName: file.fileName || ""
                      })
                    }
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canDelete && (
                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={remove.isPending}
                        onClick={() => setPendingDelete(file.fileName || "")}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>

      <DeleteConfirmDialog
        open={pendingDelete !== null}
        fileName={pendingDelete}
        isSubmitting={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() =>
          pendingDelete &&
          remove.mutate({
            entityId,
            lifecycleName,
            fileName: pendingDelete
          })
        }
      />
    </>
  );
};
